import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  LogOut, Search, Download, Trash2, ExternalLink, RefreshCw, Filter, Plus,
  Eye, EyeOff, ArrowUp, ArrowDown, X, Save, User, Mail, Phone, Building2, FileText,
  BarChart3, Users, MessageSquare, Send, MousePointerClick, Newspaper, Mic2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { api, fileUrl, qrUrl, API_BASE } from '@/lib/api';
import { useAuth } from '@/lib/authContext';
import { AAWTA } from '@/lib/aawta';

const STATUS_ORDER = [
  'new', 'under_review', 'awaiting_info', 'approved', 'confirmed', 'published', 'declined', 'archived',
];
const STATUS_LABELS = {
  new: 'New', under_review: 'Under Review', awaiting_info: 'Awaiting Info',
  approved: 'Approved', confirmed: 'Confirmed', published: 'Published',
  declined: 'Declined', archived: 'Archived',
};
const STATUS_COLORS = {
  new: 'bg-aawta-magenta text-white',
  under_review: 'bg-amber-100 text-amber-800',
  awaiting_info: 'bg-orange-100 text-orange-800',
  approved: 'bg-emerald-100 text-emerald-800',
  confirmed: 'bg-emerald-600 text-white',
  published: 'bg-aawta-navy text-white',
  declined: 'bg-rose-100 text-rose-800',
  archived: 'bg-slate-200 text-slate-700',
};

const PARTICIPATION_TYPES = [
  'Sponsor', 'Panelist', 'Moderator', 'Exhibitor',
  'Breakout Session Leader', 'Roundtable Facilitator',
];

// ---------- Submissions Panel ----------
const SubmissionsPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState('');
  const [assignee, setAssignee] = useState('');
  const [saving, setSaving] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  const applyTemplate = (t) => {
    const name = selected?.primary_contact_name?.split(' ')[0] || 'there';
    const org = selected?.organization_name || 'your organization';
    const templates = {
      approved: {
        subject: 'You are approved — AAWTA Building the Future Workforce Summit',
        body: `Congratulations ${name},\n\nWe are pleased to confirm ${org}'s participation in the AAWTA Building the Future Workforce Summit on September 17, 2026.\n\nPlease reply to confirm and we will follow up with next steps and event-day logistics.\n\nWelcome aboard!`,
      },
      more_info: {
        subject: 'More information needed — AAWTA Summit application',
        body: `Hi ${name},\n\nThank you for your submission on behalf of ${org}. We would like to request a few additional details to complete our review:\n\n• \n• \n\nPlease reply at your earliest convenience.`,
      },
      received: {
        subject: 'Application received — AAWTA Building the Future Workforce Summit',
        body: `Hi ${name},\n\nThank you for submitting ${org}'s participation form for the AAWTA Building the Future Workforce Summit. Our team is reviewing it now and will follow up with next steps shortly.`,
      },
      not_selected: {
        subject: 'Update on your AAWTA Summit application',
        body: `Hi ${name},\n\nThank you for ${org}'s interest in the AAWTA Building the Future Workforce Summit. After careful review, we are unable to move forward with this submission at this time. We appreciate your time and hope to stay in touch about future opportunities.`,
      },
      instructions: {
        subject: 'Event-day instructions — AAWTA Summit, September 17, 2026',
        body: `Hi ${name},\n\nWe are looking forward to hosting ${org} at the AAWTA Building the Future Workforce Summit on September 17, 2026 at Stone Ridge Event Center, 1750 Stone Ridge Drive, Suite A, Stone Mountain, GA 30083.\n\nArrival: 8:00 AM for check-in and breakfast\nAttire: Business or business-casual\nParking: Complimentary on-site\n\nIf you have any questions, please reply directly.`,
      },
    };
    const tpl = templates[t];
    if (tpl) { setEmailSubject(tpl.subject); setEmailBody(tpl.body); }
  };

  const sendEmail = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) { toast.error('Subject and body required'); return; }
    setSendingEmail(true);
    try {
      const { data } = await api.post(`/admin/submissions/${selected.id}/email`, { subject: emailSubject, body: emailBody });
      toast.success('Email sent');
      setEmailSubject(''); setEmailBody('');
      const updated = { ...selected, email_history: [...(selected.email_history || []), data.entry] };
      setSelected(updated);
      setItems((prev) => prev.map((it) => (it.id === updated.id ? updated : it)));
    } catch { toast.error('Email failed'); }
    finally { setSendingEmail(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (type) params.type = type;
      if (status) params.status = status;
      const { data } = await api.get('/admin/submissions', { params });
      setItems(data);
    } catch {
      toast.error('Failed to load submissions');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, type, status]);

  const doExport = async () => {
    try {
      const { data } = await api.get('/admin/submissions.csv', { responseType: 'blob' });
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url; a.download = 'aawta_submissions.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const updateSelected = async (patch) => {
    if (!selected) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/submissions/${selected.id}`, patch);
      setSelected(data);
      setItems((prev) => prev.map((it) => (it.id === data.id ? data : it)));
      toast.success('Saved');
      if (patch.note) setNote('');
      if (patch.assigned_to !== undefined) setAssignee(data.assigned_to || '');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const deleteSelected = async () => {
    if (!selected) return;
    if (!window.confirm('Delete this submission? This cannot be undone.')) return;
    try {
      await api.delete(`/admin/submissions/${selected.id}`);
      setItems((prev) => prev.filter((it) => it.id !== selected.id));
      setSelected(null);
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-aawta-muted" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search org, name, email…" data-testid="admin-search" className="pl-9 rounded-lg" />
        </div>
        <select value={type} onChange={(e) => setType(e.target.value)} data-testid="admin-filter-type" className="rounded-lg border border-aawta-border bg-white h-10 px-3 text-sm">
          <option value="">All types</option>
          {PARTICIPATION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-filter-status" className="rounded-lg border border-aawta-border bg-white h-10 px-3 text-sm">
          <option value="">All statuses</option>
          {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
        </select>
        <Button onClick={load} variant="outline" data-testid="admin-refresh" className="rounded-full border-aawta-border">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
        <Button onClick={doExport} data-testid="admin-export" className="rounded-full bg-aawta-navy hover:bg-aawta-navy-deep text-white">
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </div>

      <div className="rounded-2xl border border-aawta-border bg-white overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 text-[11px] uppercase tracking-wider font-semibold text-aawta-muted bg-aawta-surface">
          <div className="col-span-3">Organization</div>
          <div className="col-span-3">Contact</div>
          <div className="col-span-3">Type</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1 text-right">Submitted</div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-aawta-muted">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-aawta-muted">No submissions yet.</div>
        ) : items.map((it) => (
          <button
            key={it.id}
            onClick={() => { setSelected(it); setAssignee(it.assigned_to || ''); }}
            data-testid={`submission-row-${it.id}`}
            className="w-full text-left grid grid-cols-1 md:grid-cols-12 gap-3 px-5 py-4 border-t border-aawta-border hover:bg-aawta-surface transition-colors"
          >
            <div className="md:col-span-3">
              <div className="font-display font-semibold text-aawta-navy">{it.organization_name}</div>
              <div className="text-[12px] text-aawta-muted">{it.job_title || '—'}</div>
            </div>
            <div className="md:col-span-3">
              <div className="text-[14px] text-aawta-navy">{it.primary_contact_name}</div>
              <div className="text-[12px] text-aawta-muted truncate">{it.email}</div>
            </div>
            <div className="md:col-span-3 flex flex-wrap gap-1.5">
              {(it.participation_types || []).map((t) => (
                <span key={t} className="text-[10.5px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-aawta-magenta/10 text-aawta-magenta">{t}</span>
              ))}
            </div>
            <div className="md:col-span-2">
              <span className={`text-[10.5px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${STATUS_COLORS[it.status] || 'bg-slate-100'}`}>
                {STATUS_LABELS[it.status] || it.status}
              </span>
            </div>
            <div className="md:col-span-1 text-[12px] text-aawta-muted md:text-right">
              {new Date(it.created_at).toLocaleDateString()}
            </div>
          </button>
        ))}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl text-aawta-navy">
                  {selected.organization_name}
                </DialogTitle>
                <DialogDescription>
                  {selected.primary_contact_name} · {selected.email}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 mt-2">
                <div className="flex flex-wrap items-center gap-2">
                  {(selected.participation_types || []).map((t) => (
                    <span key={t} className="text-[10.5px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-aawta-magenta/10 text-aawta-magenta">{t}</span>
                  ))}
                  <span className={`text-[10.5px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${STATUS_COLORS[selected.status] || 'bg-slate-100'}`}>
                    {STATUS_LABELS[selected.status] || selected.status}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[14px]">
                  {[
                    ['Job Title', selected.job_title], ['Phone', selected.phone],
                    ['Website', selected.company_website], ['Sponsorship Level', selected.sponsorship_level],
                    ['Preferred Panel', selected.preferred_panel], ['Session Title', selected.session_title],
                    ['Booth Requirements', selected.booth_requirements],
                    ['Roundtable Topic', selected.roundtable_topic],
                  ].filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} className="p-3 rounded-lg bg-aawta-surface border border-aawta-border">
                      <div className="text-[10.5px] uppercase tracking-wider text-aawta-muted font-semibold">{k}</div>
                      <div className="mt-1 text-aawta-navy">{v}</div>
                    </div>
                  ))}
                </div>

                {selected.organization_description && (
                  <div className="p-4 rounded-lg bg-white border border-aawta-border">
                    <div className="text-[10.5px] uppercase tracking-wider text-aawta-muted font-semibold">Organization Description</div>
                    <div className="mt-1.5 whitespace-pre-wrap">{selected.organization_description}</div>
                  </div>
                )}
                {selected.additional_notes && (
                  <div className="p-4 rounded-lg bg-white border border-aawta-border">
                    <div className="text-[10.5px] uppercase tracking-wider text-aawta-muted font-semibold">Additional Notes</div>
                    <div className="mt-1.5 whitespace-pre-wrap">{selected.additional_notes}</div>
                  </div>
                )}

                {selected.files && Object.keys(selected.files).length > 0 && (
                  <div className="p-4 rounded-lg bg-aawta-surface border border-aawta-border">
                    <div className="text-[10.5px] uppercase tracking-wider text-aawta-muted font-semibold mb-2">Uploaded Files</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {Object.entries(selected.files).map(([k, v]) => (
                        <a key={k} href={fileUrl(v.id)} target="_blank" rel="noopener noreferrer"
                          data-testid={`file-${k}-${selected.id}`}
                          className="group p-3 rounded-lg bg-white border border-aawta-border hover:border-aawta-magenta transition-colors flex flex-col items-start gap-2">
                          <div className="text-[10.5px] uppercase tracking-wider text-aawta-muted font-semibold">{k}</div>
                          {v.content_type?.startsWith('image/') ? (
                            <img src={fileUrl(v.id)} alt={k} className="w-full h-24 object-cover rounded" />
                          ) : (
                            <div className="flex items-center gap-2 text-aawta-navy text-sm truncate">
                              <FileText className="h-4 w-4 shrink-0" />
                              <span className="truncate">{v.filename}</span>
                            </div>
                          )}
                          <span className="text-[11px] text-aawta-magenta font-semibold inline-flex items-center gap-1">
                            <Download className="h-3 w-3" /> Download
                          </span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Status</Label>
                    <select
                      value={selected.status}
                      onChange={(e) => updateSelected({ status: e.target.value })}
                      data-testid="submission-status-select"
                      className="mt-1.5 w-full rounded-lg border border-aawta-border bg-white h-10 px-3 text-sm"
                    >
                      {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Assigned to</Label>
                    <div className="mt-1.5 flex gap-2">
                      <Input value={assignee} onChange={(e) => setAssignee(e.target.value)} placeholder="team member email or name" className="rounded-lg" data-testid="submission-assignee" />
                      <Button variant="outline" onClick={() => updateSelected({ assigned_to: assignee })} className="rounded-full">Save</Button>
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Internal notes</Label>
                  <div className="mt-2 space-y-2">
                    {(selected.internal_notes || []).map((n) => (
                      <div key={n.id} className="p-3 rounded-lg bg-aawta-surface border border-aawta-border">
                        <div className="text-[11px] text-aawta-muted">{n.author} · {new Date(n.created_at).toLocaleString()}</div>
                        <div className="mt-1 text-[14px] text-aawta-navy whitespace-pre-wrap">{n.text}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" rows={2} data-testid="submission-note-input" className="rounded-lg resize-none" />
                    <Button disabled={!note.trim() || saving} onClick={() => updateSelected({ note })} className="rounded-full bg-aawta-magenta hover:bg-aawta-magenta-hover text-white shrink-0" data-testid="submission-note-add">Add note</Button>
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-aawta-surface border border-aawta-border">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="font-display font-semibold text-aawta-navy text-[15px]">Send email to applicant</div>
                    <select
                      onChange={(e) => { if (e.target.value) { applyTemplate(e.target.value); e.target.value = ''; } }}
                      data-testid="submission-email-template"
                      className="rounded-lg border border-aawta-border bg-white h-8 px-2 text-[12.5px]"
                      defaultValue=""
                    >
                      <option value="">Quick template…</option>
                      <option value="received">Application Received</option>
                      <option value="more_info">More Information Needed</option>
                      <option value="approved">Approved</option>
                      <option value="instructions">Event Instructions</option>
                      <option value="not_selected">Not Selected</option>
                    </select>
                  </div>
                  <div className="mt-3 space-y-2">
                    <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} placeholder="Subject" data-testid="submission-email-subject" className="rounded-lg" />
                    <Textarea value={emailBody} onChange={(e) => setEmailBody(e.target.value)} rows={6} placeholder="Message body" data-testid="submission-email-body" className="rounded-lg resize-none" />
                    <Button onClick={sendEmail} disabled={sendingEmail} data-testid="submission-email-send" className="rounded-full bg-aawta-navy hover:bg-aawta-navy-deep text-white">
                      <Mail className="h-4 w-4 mr-2" /> {sendingEmail ? 'Sending…' : `Send to ${selected.email}`}
                    </Button>
                  </div>

                  {(selected.email_history || []).length > 0 && (
                    <div className="mt-4 pt-4 border-t border-aawta-border" data-testid="submission-email-history">
                      <div className="text-[11px] uppercase tracking-wider text-aawta-muted font-semibold mb-2">
                        Communication history ({selected.email_history.length})
                      </div>
                      <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                        {[...selected.email_history].reverse().map((e) => (
                          <details key={e.id} className="rounded-lg bg-white border border-aawta-border p-3 text-[13px]">
                            <summary className="cursor-pointer flex flex-wrap items-center gap-x-3 gap-y-1 text-aawta-navy">
                              <span className="font-semibold truncate">{e.subject}</span>
                              <span className="text-[11.5px] text-aawta-muted">
                                {new Date(e.sent_at).toLocaleString()} · to {e.to} · by {e.sent_by}
                              </span>
                            </summary>
                            <div className="mt-2 whitespace-pre-wrap text-aawta-muted text-[13px] leading-relaxed">{e.body}</div>
                          </details>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter className="mt-4 gap-2 sm:gap-2">
                <Button variant="ghost" onClick={deleteSelected} className="rounded-full text-rose-600 hover:bg-rose-50" data-testid="submission-delete">
                  <Trash2 className="h-4 w-4 mr-2" /> Delete
                </Button>
                <Button onClick={() => setSelected(null)} variant="outline" className="rounded-full">Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------- Generic CMS panel (Sponsors / Exhibitors / Speakers / Gallery) ----------
const CmsPanel = ({ resource, label, singular, fields, uploadFolder, testIdKey }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | new item | existing item
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/${resource}`);
      setItems(data);
    } catch { toast.error(`Failed to load ${label}`); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [resource]);

  const startNew = () => setEditing({ display_order: items.length, published: false });
  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.name || item.title}"?`)) return;
    try {
      await api.delete(`/admin/${resource}/${item.id}`);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };
  const togglePublish = async (item) => {
    try {
      const { data } = await api.patch(`/admin/${resource}/${item.id}`, { ...item, published: !item.published });
      setItems((prev) => prev.map((x) => (x.id === item.id ? data : x)));
    } catch { toast.error('Update failed'); }
  };
  const move = async (item, delta) => {
    const newOrder = (item.display_order || 0) + delta;
    try {
      const { data } = await api.patch(`/admin/${resource}/${item.id}`, { ...item, display_order: newOrder });
      setItems((prev) => [...prev.map((x) => (x.id === item.id ? data : x))].sort((a, b) => (a.display_order || 0) - (b.display_order || 0)));
    } catch { toast.error('Update failed'); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...editing };
      delete payload._newFiles;
      const isNew = !editing.id;
      const method = isNew ? 'post' : 'patch';
      const url = isNew ? `/admin/${resource}` : `/admin/${resource}/${editing.id}`;
      const { data } = await api[method](url, payload);
      if (isNew) setItems((prev) => [...prev, data]);
      else setItems((prev) => prev.map((x) => (x.id === data.id ? data : x)));
      setEditing(null);
      toast.success('Saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const uploadField = async (field, file) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('folder', uploadFolder);
    try {
      const { data } = await api.post('/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setEditing((prev) => ({ ...prev, [field]: data.id }));
      toast.success('Uploaded');
    } catch { toast.error('Upload failed'); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="font-display font-semibold text-xl text-aawta-navy">{label}</div>
          <div className="text-[13px] text-aawta-muted">{items.length} total · {items.filter((x) => x.published).length} published</div>
        </div>
        <Button onClick={startNew} data-testid={`cms-new-${testIdKey}`} className="rounded-full bg-aawta-magenta hover:bg-aawta-magenta-hover text-white">
          <Plus className="h-4 w-4 mr-2" /> Add {label.slice(0, -1)}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full p-8 text-center text-aawta-muted">Loading…</div>
        ) : items.length === 0 ? (
          <div className="col-span-full p-8 text-center text-aawta-muted bg-white rounded-2xl border border-dashed border-aawta-border">
            No {label.toLowerCase()} yet. Click <strong>Add {label.slice(0, -1)}</strong> to create one.
          </div>
        ) : items.sort((a, b) => (a.display_order || 0) - (b.display_order || 0)).map((it) => {
          const imageField = fields.find((f) => f.kind === 'image');
          const imageId = imageField && it[imageField.key];
          return (
            <div key={it.id} data-testid={`cms-item-${it.id}`} className="rounded-2xl bg-white border border-aawta-border p-4 flex flex-col">
              {imageId ? (
                <img src={fileUrl(imageId)} alt="" className="w-full h-32 object-cover rounded-lg bg-aawta-surface" />
              ) : (
                <div className="w-full h-32 rounded-lg bg-aawta-surface flex items-center justify-center text-aawta-muted text-xs">No image</div>
              )}
              <div className="mt-3 font-display font-semibold text-aawta-navy truncate">{it.name || it.title}</div>
              <div className="text-[12px] text-aawta-muted truncate">{it.level || it.booth_number || it.category || it.organization || '—'}</div>
              <div className="mt-3 flex items-center gap-2">
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${it.published ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'}`}>
                  {it.published ? 'Published' : 'Draft'}
                </span>
                <span className="text-[10px] text-aawta-muted">Order: {it.display_order ?? 0}</span>
              </div>
              <div className="mt-4 pt-3 border-t border-aawta-border flex flex-wrap gap-1.5">
                <Button size="sm" variant="ghost" onClick={() => setEditing(it)} data-testid={`cms-edit-${it.id}`} className="rounded-full text-aawta-navy hover:bg-aawta-surface h-8">Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => togglePublish(it)} className="rounded-full h-8">
                  {it.published ? <><EyeOff className="h-3.5 w-3.5 mr-1" /> Unpublish</> : <><Eye className="h-3.5 w-3.5 mr-1" /> Publish</>}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => move(it, -1)} className="rounded-full h-8"><ArrowUp className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="ghost" onClick={() => move(it, 1)} className="rounded-full h-8"><ArrowDown className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="ghost" onClick={() => remove(it)} className="rounded-full h-8 text-rose-600 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl">
          {editing && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl text-aawta-navy">
                  {editing.id ? `Edit ${singular}` : `New ${singular}`}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                {fields.map((f) => (
                  <div key={f.key} className="space-y-1.5">
                    <Label>{f.label}{f.required && <span className="text-aawta-magenta">*</span>}</Label>
                    {f.kind === 'textarea' ? (
                      <Textarea value={editing[f.key] || ''} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} rows={3} className="rounded-lg resize-none" />
                    ) : f.kind === 'number' ? (
                      <Input type="number" value={editing[f.key] ?? 0} onChange={(e) => setEditing({ ...editing, [f.key]: Number(e.target.value) })} className="rounded-lg" />
                    ) : f.kind === 'boolean' ? (
                      <div className="flex items-center gap-2">
                        <input type="checkbox" checked={!!editing[f.key]} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.checked })} />
                        <span className="text-[13px] text-aawta-muted">{f.hint || 'Toggle'}</span>
                      </div>
                    ) : f.kind === 'image' || f.kind === 'video' || f.kind === 'file' ? (
                      <div className="space-y-2">
                        {editing[f.key] && (
                          f.kind === 'image' ? (
                            <img src={fileUrl(editing[f.key])} alt="" className="w-40 h-28 object-cover rounded-lg border border-aawta-border" />
                          ) : f.kind === 'video' ? (
                            <video src={fileUrl(editing[f.key])} controls className="w-full max-h-48 rounded-lg" />
                          ) : (
                            <a href={fileUrl(editing[f.key])} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[13px] text-aawta-magenta hover:underline">
                              <FileText className="h-4 w-4" /> View current file
                            </a>
                          )
                        )}
                        <input
                          type="file"
                          accept={f.kind === 'image' ? 'image/*' : f.kind === 'video' ? 'video/*' : '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.png,.jpg,.jpeg,application/pdf'}
                          onChange={(e) => e.target.files?.[0] && uploadField(f.key, e.target.files[0])}
                          data-testid={`cms-upload-${testIdKey}-${f.key}`}
                        />
                      </div>
                    ) : (
                      <Input value={editing[f.key] || ''} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} className="rounded-lg" data-testid={`cms-field-${testIdKey}-${f.key}`} />
                    )}
                    {f.hint && f.kind !== 'boolean' && <div className="text-[12px] text-aawta-muted">{f.hint}</div>}
                  </div>
                ))}
              </div>
              <DialogFooter className="mt-4 gap-2">
                <Button variant="ghost" onClick={() => setEditing(null)} className="rounded-full">Cancel</Button>
                <Button onClick={save} disabled={saving} data-testid={`cms-save-${testIdKey}`} className="rounded-full bg-aawta-magenta hover:bg-aawta-magenta-hover text-white">
                  <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving…' : 'Save'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------- Field configs ----------
const SPONSOR_FIELDS = [
  { key: 'name', label: 'Sponsor name', required: true },
  { key: 'level', label: 'Sponsorship level' },
  { key: 'description', label: 'Organization description', kind: 'textarea' },
  { key: 'company_story', label: 'Company story', kind: 'textarea' },
  { key: 'website', label: 'Website' },
  { key: 'cta_label', label: 'Call-to-action label' },
  { key: 'cta_url', label: 'Call-to-action URL' },
  { key: 'video_url', label: 'Promotional video URL (YouTube/Vimeo)' },
  { key: 'logo_file_id', label: 'Company logo', kind: 'image' },
  { key: 'banner_file_id', label: 'Featured banner image', kind: 'image' },
  { key: 'video_file_id', label: 'Promotional video upload', kind: 'video' },
  { key: 'display_order', label: 'Display order', kind: 'number' },
  { key: 'published', label: 'Published', kind: 'boolean', hint: 'Show on public Sponsor Showcase' },
];
const EXHIBITOR_FIELDS = [
  { key: 'name', label: 'Organization name', required: true },
  { key: 'booth_number', label: 'Booth number' },
  { key: 'description', label: 'Organization description', kind: 'textarea' },
  { key: 'website', label: 'Website' },
  { key: 'industry', label: 'Industry category' },
  { key: 'products', label: 'Featured products or services', kind: 'textarea' },
  { key: 'contact_name', label: 'Contact name' },
  { key: 'contact_email', label: 'Contact email' },
  { key: 'contact_phone', label: 'Contact phone' },
  { key: 'qr_link', label: 'QR code destination URL', hint: 'The QR code is auto-generated from this URL.' },
  { key: 'logo_file_id', label: 'Company logo', kind: 'image' },
  { key: 'display_order', label: 'Display order', kind: 'number' },
  { key: 'published', label: 'Published', kind: 'boolean', hint: 'Show on public Exhibitor Directory' },
];
const SPEAKER_FIELDS = [
  { key: 'name', label: 'Name', required: true },
  { key: 'title', label: 'Title' },
  { key: 'organization', label: 'Organization' },
  { key: 'biography', label: 'Biography', kind: 'textarea' },
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'website', label: 'Website' },
  { key: 'session_assignment', label: 'Session assignment' },
  { key: 'session_title', label: 'Session title' },
  { key: 'session_time', label: 'Session time' },
  { key: 'headshot_file_id', label: 'Headshot', kind: 'image' },
  { key: 'video_intro_file_id', label: 'Video introduction', kind: 'video' },
  { key: 'display_order', label: 'Display order', kind: 'number' },
  { key: 'published', label: 'Published', kind: 'boolean', hint: 'Show on public Speakers page' },
];
const GALLERY_FIELDS = [
  { key: 'title', label: 'Title', required: true },
  { key: 'caption', label: 'Caption', kind: 'textarea' },
  { key: 'category', label: 'Category', hint: 'Sponsors / Speakers / Exhibitors / Summit Highlights / Partner Stories / Behind the Scenes / Promotional Media' },
  { key: 'alt_text', label: 'Alt text (accessibility)' },
  { key: 'media_file_id', label: 'Image or video', kind: 'image' },
  { key: 'display_order', label: 'Display order', kind: 'number' },
  { key: 'published', label: 'Published', kind: 'boolean', hint: 'Show on public Gallery' },
];
const DOWNLOAD_FIELDS = [
  { key: 'title', label: 'Document title', required: true, hint: 'Displayed publicly on the Download Center card' },
  { key: 'tab', label: 'Short tag / label', hint: 'e.g. Speaker Guide, Sponsorship Kit' },
  { key: 'description', label: 'Description', kind: 'textarea' },
  { key: 'kind', label: 'Kind', hint: 'PDF, DOCX, PNG, JPG, Canva, External' },
  { key: 'file_id', label: 'Upload file', kind: 'file', hint: 'PDF/DOCX/PPTX/PNG/JPG. Leave empty if using an external URL below.' },
  { key: 'external_url', label: 'External URL (optional)', hint: 'Use instead of file upload for Canva/Gamma/YouTube links.' },
  { key: 'icon', label: 'Icon (lucide name, optional)', hint: 'FileText, Briefcase, Store, Mic2, ImageIcon, Presentation, Share2' },
  { key: 'display_order', label: 'Display order', kind: 'number' },
  { key: 'published', label: 'Published', kind: 'boolean', hint: 'Show on the public Download Center' },
];

// ---------- Applications Panel (Volunteer / Media) ----------
const APP_STATUS = ['new', 'pending', 'approved', 'declined', 'archived'];
const APP_STATUS_LABELS = { new: 'New', pending: 'Pending', approved: 'Approved', declined: 'Declined', archived: 'Archived' };
const APP_STATUS_COLORS = {
  new: 'bg-aawta-magenta text-white', pending: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-800', declined: 'bg-rose-100 text-rose-800',
  archived: 'bg-slate-200 text-slate-700',
};

const ApplicationsPanel = ({ resource, kind, assignmentLabel, assignmentOptions }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (status) params.status = status;
      const { data } = await api.get(`/admin/${resource}`, { params });
      setItems(data);
    } catch { toast.error(`Failed to load ${kind}`); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [resource, q, status]);

  const updateSelected = async (patch) => {
    if (!selected) return;
    try {
      const { data } = await api.patch(`/admin/${resource}/${selected.id}`, patch);
      setSelected(data);
      setItems((prev) => prev.map((it) => (it.id === data.id ? data : it)));
      toast.success('Saved');
      if (patch.note) setNote('');
    } catch { toast.error('Save failed'); }
  };

  const deleteSelected = async () => {
    if (!selected || !window.confirm('Delete this application?')) return;
    try {
      await api.delete(`/admin/${resource}/${selected.id}`);
      setItems((p) => p.filter((it) => it.id !== selected.id));
      setSelected(null);
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  const doExport = async () => {
    try {
      const { data } = await api.get(`/admin/${resource}.csv`, { responseType: 'blob' });
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url; a.download = `aawta_${resource}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const applyTemplate = (t) => {
    const name = selected?.full_name?.split(' ')[0] || 'there';
    const templates = {
      approved: {
        subject: 'You are approved — AAWTA Building the Future Workforce Summit',
        body: `Congratulations ${name},\n\nWe are pleased to inform you that your application has been approved for the AAWTA Building the Future Workforce Summit on September 17, 2026.\n\nPlease reply to this email to confirm your participation. Additional event-day instructions will follow.\n\nWelcome aboard!`,
      },
      more_info: {
        subject: 'More information needed — AAWTA Summit application',
        body: `Hi ${name},\n\nThank you for applying. We would like to request a few additional details to complete our review:\n\n• \n• \n\nPlease reply at your earliest convenience.`,
      },
      received: {
        subject: 'Application received — AAWTA Building the Future Workforce Summit',
        body: `Hi ${name},\n\nThank you for submitting your application to the AAWTA Building the Future Workforce Summit. Our team is reviewing it now and will follow up with next steps shortly.\n\nWe appreciate your interest in supporting our workforce mission.`,
      },
      not_selected: {
        subject: 'Update on your AAWTA Summit application',
        body: `Hi ${name},\n\nThank you for your interest in the AAWTA Building the Future Workforce Summit. After careful review, we are unable to move forward with your application at this time. We received a strong volume of qualified applicants and had limited spots available.\n\nWe truly appreciate your time and hope to stay connected for future opportunities.`,
      },
      instructions: {
        subject: 'Event-day instructions — AAWTA Summit, September 17, 2026',
        body: `Hi ${name},\n\nWe are looking forward to seeing you at the AAWTA Building the Future Workforce Summit on September 17, 2026 at Stone Ridge Event Center, 1750 Stone Ridge Drive, Suite A, Stone Mountain, GA 30083.\n\nArrival: 8:00 AM for check-in and breakfast\nAttire: Business or business-casual\nParking: Complimentary on-site\n\nIf you have any questions, please reply to this email.`,
      },
    };
    const tpl = templates[t];
    if (tpl) { setEmailSubject(tpl.subject); setEmailBody(tpl.body); }
  };

  const sendEmail = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) { toast.error('Subject and body required'); return; }
    setSendingEmail(true);
    try {
      const { data } = await api.post(`/admin/${resource}/${selected.id}/email`, { subject: emailSubject, body: emailBody });
      toast.success('Email sent');
      setEmailSubject(''); setEmailBody('');
      const updated = { ...selected, email_history: [...(selected.email_history || []), data.entry] };
      setSelected(updated);
      setItems((prev) => prev.map((it) => (it.id === updated.id ? updated : it)));
    } catch { toast.error('Email failed'); }
    finally { setSendingEmail(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-aawta-muted" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${kind}…`} data-testid={`admin-${resource}-search`} className="pl-9 rounded-lg" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid={`admin-${resource}-filter-status`} className="rounded-lg border border-aawta-border bg-white h-10 px-3 text-sm">
          <option value="">All statuses</option>
          {APP_STATUS.map((s) => <option key={s} value={s}>{APP_STATUS_LABELS[s]}</option>)}
        </select>
        <Button onClick={load} variant="outline" className="rounded-full border-aawta-border"><RefreshCw className="h-4 w-4 mr-2" /> Refresh</Button>
        <Button onClick={doExport} data-testid={`admin-${resource}-export`} className="rounded-full bg-aawta-navy hover:bg-aawta-navy-deep text-white"><Download className="h-4 w-4 mr-2" /> Export CSV</Button>
      </div>

      <div className="rounded-2xl border border-aawta-border bg-white overflow-hidden">
        {loading ? <div className="p-8 text-center text-aawta-muted">Loading…</div>
        : items.length === 0 ? <div className="p-8 text-center text-aawta-muted">No {kind} yet.</div>
        : items.map((it) => (
          <button key={it.id} onClick={() => setSelected(it)} data-testid={`${resource}-row-${it.id}`} className="w-full text-left grid grid-cols-1 md:grid-cols-12 gap-3 px-5 py-4 border-b last:border-b-0 border-aawta-border hover:bg-aawta-surface">
            <div className="md:col-span-3">
              <div className="font-display font-semibold text-aawta-navy">{it.full_name}</div>
              <div className="text-[12px] text-aawta-muted truncate">{it.email}</div>
            </div>
            <div className="md:col-span-3 text-[13px] text-aawta-navy">{it.organization || it.outlet || '—'}</div>
            <div className="md:col-span-3 text-[12.5px] text-aawta-muted">{it.preferred_role || it.coverage_type || '—'}</div>
            <div className="md:col-span-2">
              <span className={`text-[10.5px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${APP_STATUS_COLORS[it.status] || 'bg-slate-100'}`}>{APP_STATUS_LABELS[it.status] || it.status}</span>
            </div>
            <div className="md:col-span-1 text-[12px] text-aawta-muted md:text-right">{new Date(it.created_at).toLocaleDateString()}</div>
          </button>
        ))}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl text-aawta-navy">{selected.full_name}</DialogTitle>
                <DialogDescription>{selected.email} · Submitted {new Date(selected.created_at).toLocaleString()}</DialogDescription>
              </DialogHeader>
              <div className="mt-2 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {Object.entries(selected).filter(([k, v]) =>
                    !['id','_id','internal_notes','email_history','logo_file_id','headshot_file_id','status','assigned_to','assigned_type','confirmation_status','display_on_summit','created_at'].includes(k)
                    && v !== null && v !== '' && !(Array.isArray(v) && v.length===0)
                  ).map(([k, v]) => (
                    <div key={k} className="p-3 rounded-lg bg-aawta-surface border border-aawta-border">
                      <div className="text-[10.5px] uppercase tracking-wider text-aawta-muted font-semibold">{k.replace(/_/g,' ')}</div>
                      <div className="mt-1 text-aawta-navy text-[13.5px] whitespace-pre-wrap">{Array.isArray(v) ? v.join(', ') : String(v)}</div>
                    </div>
                  ))}
                </div>

                {(selected.logo_file_id || selected.headshot_file_id) && (
                  <div className="p-4 rounded-lg bg-aawta-surface border border-aawta-border">
                    <div className="text-[10.5px] uppercase tracking-wider text-aawta-muted font-semibold mb-2">
                      {selected.headshot_file_id ? 'Headshot' : 'Logo'}
                    </div>
                    <img
                      src={fileUrl(selected.headshot_file_id || selected.logo_file_id)}
                      alt="applicant asset"
                      className="max-h-40 rounded-lg border border-aawta-border bg-white"
                      data-testid={`${resource}-asset-preview`}
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label>Status</Label>
                    <select value={selected.status} onChange={(e) => updateSelected({ status: e.target.value })} data-testid={`${resource}-status-select`} className="mt-1.5 w-full rounded-lg border border-aawta-border bg-white h-10 px-3 text-sm">
                      {APP_STATUS.map((s) => <option key={s} value={s}>{APP_STATUS_LABELS[s]}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>{assignmentLabel}</Label>
                    <select value={selected.assigned_type || ''} onChange={(e) => updateSelected({ assigned_type: e.target.value })} data-testid={`${resource}-assignment-select`} className="mt-1.5 w-full rounded-lg border border-aawta-border bg-white h-10 px-3 text-sm">
                      <option value="">— None —</option>
                      {assignmentOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Confirmation status</Label>
                    <select value={selected.confirmation_status || 'not_sent'} onChange={(e) => updateSelected({ confirmation_status: e.target.value })} className="mt-1.5 w-full rounded-lg border border-aawta-border bg-white h-10 px-3 text-sm">
                      <option value="not_sent">Not sent</option>
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="declined">Declined</option>
                    </select>
                  </div>
                </div>

                <label className="flex items-start gap-3 p-3 rounded-lg bg-white border border-aawta-border cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!selected.display_on_summit}
                    onChange={(e) => updateSelected({ display_on_summit: e.target.checked })}
                    disabled={selected.status !== 'approved'}
                    data-testid={`${resource}-display-toggle`}
                    className="mt-1"
                  />
                  <div>
                    <div className="text-[13.5px] font-semibold text-aawta-navy">Display on Summit Page</div>
                    <div className="text-[12px] text-aawta-muted">
                      {selected.status === 'approved'
                        ? 'When enabled, this profile appears in the public "Meet Our" section.'
                        : 'Set status to Approved to enable public display.'}
                    </div>
                  </div>
                </label>

                <div>
                  <Label>Internal notes</Label>
                  <div className="mt-2 space-y-2 max-h-40 overflow-y-auto pr-2">
                    {(selected.internal_notes || []).map((n) => (
                      <div key={n.id} className="p-3 rounded-lg bg-aawta-surface border border-aawta-border">
                        <div className="text-[11px] text-aawta-muted">{n.author} · {new Date(n.created_at).toLocaleString()}</div>
                        <div className="mt-1 text-[14px] text-aawta-navy whitespace-pre-wrap">{n.text}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" rows={2} data-testid={`${resource}-note-input`} className="rounded-lg resize-none" />
                    <Button disabled={!note.trim()} onClick={() => updateSelected({ note })} className="rounded-full bg-aawta-magenta hover:bg-aawta-magenta-hover text-white shrink-0">Add note</Button>
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-aawta-surface border border-aawta-border">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="font-display font-semibold text-aawta-navy text-[15px]">Send email to applicant</div>
                    <select
                      onChange={(e) => { if (e.target.value) { applyTemplate(e.target.value); e.target.value = ''; } }}
                      data-testid={`${resource}-email-template`}
                      className="rounded-lg border border-aawta-border bg-white h-8 px-2 text-[12.5px]"
                      defaultValue=""
                    >
                      <option value="">Quick template…</option>
                      <option value="received">Application Received</option>
                      <option value="more_info">More Information Needed</option>
                      <option value="approved">Approved</option>
                      <option value="instructions">Event Instructions</option>
                      <option value="not_selected">Not Selected</option>
                    </select>
                  </div>
                  <div className="mt-3 space-y-2">
                    <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} placeholder="Subject" data-testid={`${resource}-email-subject`} className="rounded-lg" />
                    <Textarea value={emailBody} onChange={(e) => setEmailBody(e.target.value)} rows={6} placeholder="Message body" data-testid={`${resource}-email-body`} className="rounded-lg resize-none" />
                    <Button onClick={sendEmail} disabled={sendingEmail} data-testid={`${resource}-email-send`} className="rounded-full bg-aawta-navy hover:bg-aawta-navy-deep text-white">
                      <Mail className="h-4 w-4 mr-2" /> {sendingEmail ? 'Sending…' : `Send to ${selected.email}`}
                    </Button>
                  </div>

                  {(selected.email_history || []).length > 0 && (
                    <div className="mt-4 pt-4 border-t border-aawta-border" data-testid={`${resource}-email-history`}>
                      <div className="text-[11px] uppercase tracking-wider text-aawta-muted font-semibold mb-2">
                        Communication history ({selected.email_history.length})
                      </div>
                      <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                        {[...selected.email_history].reverse().map((e) => (
                          <details key={e.id} className="rounded-lg bg-white border border-aawta-border p-3 text-[13px]">
                            <summary className="cursor-pointer flex flex-wrap items-center gap-x-3 gap-y-1 text-aawta-navy">
                              <span className="font-semibold truncate">{e.subject}</span>
                              <span className="text-[11.5px] text-aawta-muted">
                                {new Date(e.sent_at).toLocaleString()} · to {e.to} · by {e.sent_by}
                              </span>
                            </summary>
                            <div className="mt-2 whitespace-pre-wrap text-aawta-muted text-[13px] leading-relaxed">{e.body}</div>
                          </details>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter className="mt-4 gap-2">
                <Button variant="ghost" onClick={deleteSelected} className="rounded-full text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4 mr-2" /> Delete</Button>
                <Button onClick={() => setSelected(null)} variant="outline" className="rounded-full">Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const VOLUNTEER_ROLES = [
  'Registration and Check-In', 'Speaker Support', 'Exhibitor and Sponsor Support',
  'Ballroom and Session Support', 'Hospitality', 'VIP and Reception Support',
  'Media and Red Carpet Support', 'Setup and Breakdown', 'General Event Support',
];
const MEDIA_CREDENTIALS = [
  'Press Pass', 'Photographer Pass', 'Videographer Pass', 'Social Media Creator Pass',
  'VIP Media Pass', 'Broadcast Crew', 'General Media Access',
];

// ---------- Overview / Analytics Panel ----------
const StatCard = ({ icon: Icon, label, value, sub, testId, tone = 'navy' }) => {
  const toneMap = {
    navy: 'bg-aawta-navy text-white',
    magenta: 'bg-aawta-magenta text-white',
    emerald: 'bg-emerald-600 text-white',
    slate: 'bg-slate-900 text-white',
  };
  return (
    <div data-testid={testId} className="rounded-2xl bg-white border border-aawta-border p-5 flex items-start gap-4 hover:shadow-aawta-elevated transition-shadow">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${toneMap[tone]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-aawta-muted font-semibold">{label}</div>
        <div className="font-display font-bold text-2xl text-aawta-navy leading-none mt-1">{value ?? '—'}</div>
        {sub && <div className="text-[12px] text-aawta-muted mt-1">{sub}</div>}
      </div>
    </div>
  );
};

const BreakdownList = ({ title, items, testId, emptyLabel = 'No data yet' }) => (
  <div data-testid={testId} className="rounded-2xl bg-white border border-aawta-border p-5">
    <div className="text-[11px] uppercase tracking-wider text-aawta-muted font-semibold mb-3">{title}</div>
    {(!items || items.length === 0) ? (
      <div className="text-[13px] text-aawta-muted">{emptyLabel}</div>
    ) : (
      <div className="space-y-2">
        {items.map((it, i) => {
          const total = Math.max(1, items.reduce((s, x) => s + x.count, 0));
          const pct = Math.round((it.count / total) * 100);
          return (
            <div key={`${it.key}-${i}`} className="flex items-center gap-3">
              <div className="text-[13.5px] text-aawta-navy min-w-[110px] truncate">{it.key}</div>
              <div className="flex-1 h-2 rounded-full bg-aawta-surface overflow-hidden">
                <div className="h-full bg-aawta-magenta rounded-full" style={{ width: `${pct}%` }} />
              </div>
              <div className="text-[12.5px] font-semibold text-aawta-navy tabular-nums w-10 text-right">{it.count}</div>
            </div>
          );
        })}
      </div>
    )}
  </div>
);

const OverviewPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/analytics');
      setData(data);
    } catch { toast.error('Failed to load analytics'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) {
    return <div className="p-8 text-center text-aawta-muted">Loading analytics…</div>;
  }

  const sub = (n) => `${n} in the last 30 days`;
  return (
    <div className="space-y-6" data-testid="overview-panel">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="font-display font-semibold text-xl text-aawta-navy">Summit Overview</div>
          <div className="text-[13px] text-aawta-muted">Live snapshot — updated {new Date(data.generated_at).toLocaleString()}</div>
        </div>
        <Button onClick={load} variant="outline" data-testid="overview-refresh" className="rounded-full border-aawta-border">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard testId="stat-submissions" icon={MessageSquare} label="Partner submissions" value={data.submissions.total} sub={sub(data.submissions.last_30d)} tone="navy" />
        <StatCard testId="stat-volunteer" icon={Users} label="Volunteer apps" value={data.volunteer.total} sub={`${data.volunteer.approved} approved · ${data.volunteer.public} public`} tone="magenta" />
        <StatCard testId="stat-media" icon={Newspaper} label="Media apps" value={data.media.total} sub={`${data.media.approved} approved · ${data.media.public} public`} tone="magenta" />
        <StatCard testId="stat-newsletter" icon={Send} label="Newsletter subs" value={data.newsletter.total} sub={sub(data.newsletter.last_30d)} tone="emerald" />
        <StatCard testId="stat-downloads" icon={Download} label="Downloads published" value={data.downloads.published} sub={`${data.downloads.total} total`} tone="slate" />
        <StatCard testId="stat-clicks" icon={MousePointerClick} label="Download clicks" value={data.downloads.total_clicks} sub="All-time" tone="slate" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BreakdownList testId="breakdown-status" title="Partner submissions — by status" items={data.submissions.by_status} />
        <BreakdownList testId="breakdown-type" title="Partner submissions — by participation type" items={data.submissions.by_type} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl bg-white border border-aawta-border p-5" data-testid="top-downloads">
          <div className="text-[11px] uppercase tracking-wider text-aawta-muted font-semibold mb-3">Top downloaded documents</div>
          {(!data.downloads.top || data.downloads.top.length === 0) ? (
            <div className="text-[13px] text-aawta-muted">No download clicks tracked yet. Publish a document and share the site to see traffic.</div>
          ) : (
            <div className="space-y-2">
              {data.downloads.top.map((d) => (
                <div key={d.id} className="flex items-center justify-between gap-3 py-2 border-b last:border-b-0 border-aawta-border">
                  <div className="text-[13.5px] text-aawta-navy truncate">{d.title}</div>
                  <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-aawta-magenta/10 text-aawta-magenta tabular-nums">
                    {d.click_count || 0} clicks
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="rounded-2xl bg-white border border-aawta-border p-5" data-testid="cms-summary">
          <div className="text-[11px] uppercase tracking-wider text-aawta-muted font-semibold mb-3">Published on public site</div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { key: 'Sponsors', v: data.cms_published.sponsors },
              { key: 'Speakers', v: data.cms_published.speakers },
              { key: 'Exhibitors', v: data.cms_published.exhibitors },
              { key: 'Gallery items', v: data.cms_published.gallery },
            ].map((row) => (
              <div key={row.key} className="p-3 rounded-lg bg-aawta-surface border border-aawta-border">
                <div className="text-[11px] uppercase tracking-wider text-aawta-muted font-semibold">{row.key}</div>
                <div className="font-display font-bold text-xl text-aawta-navy mt-1">{row.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------- Newsletter Panel ----------
const NewsletterPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [broadcasts, setBroadcasts] = useState([]);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: subs }, { data: hist }] = await Promise.all([
        api.get('/admin/newsletter'),
        api.get('/admin/newsletter/broadcasts'),
      ]);
      setItems(subs);
      setBroadcasts(hist);
    } catch { toast.error('Failed to load subscribers'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm('Remove this subscriber?')) return;
    try {
      await api.delete(`/admin/newsletter/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
      toast.success('Removed');
    } catch { toast.error('Delete failed'); }
  };

  const doExport = () => {
    const rows = [['Email', 'Signed up at'], ...items.map((s) => [s.email, s.created_at])];
    const csv = rows.map((r) => r.map((c) => `"${String(c || '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'aawta_newsletter.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const sendBroadcast = async () => {
    if (!subject.trim() || !body.trim()) { toast.error('Subject and body required'); return; }
    if (!window.confirm(`Send this broadcast to ${items.length} subscribers?`)) return;
    setSending(true);
    try {
      const { data } = await api.post('/admin/newsletter/broadcast', { subject, body });
      toast.success(`Broadcast sent to ${data.recipient_count} subscribers`);
      setSubject(''); setBody('');
      setBroadcasts((prev) => [data.broadcast, ...prev]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Broadcast failed');
    } finally { setSending(false); }
  };

  const filtered = q ? items.filter((x) => x.email.toLowerCase().includes(q.toLowerCase())) : items;

  return (
    <div className="space-y-5" data-testid="newsletter-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-display font-semibold text-xl text-aawta-navy">Newsletter Subscribers</div>
          <div className="text-[13px] text-aawta-muted">{items.length} total · footer subscribe form</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-aawta-muted" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email…" data-testid="newsletter-search" className="pl-9 rounded-lg w-64" />
          </div>
          <Button onClick={load} variant="outline" className="rounded-full border-aawta-border"><RefreshCw className="h-4 w-4 mr-2" /> Refresh</Button>
          <Button onClick={doExport} data-testid="newsletter-export" className="rounded-full bg-aawta-navy hover:bg-aawta-navy-deep text-white"><Download className="h-4 w-4 mr-2" /> Export CSV</Button>
        </div>
      </div>

      <div className="p-5 rounded-2xl bg-white border border-aawta-border" data-testid="newsletter-broadcast">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="font-display font-semibold text-aawta-navy text-[15px]">Compose broadcast</div>
            <div className="text-[12.5px] text-aawta-muted mt-0.5">Sends via Resend to all {items.length} subscribers.</div>
          </div>
          <span className="text-[10.5px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-aawta-magenta/10 text-aawta-magenta">
            {items.length} recipients
          </span>
        </div>
        <div className="mt-3 space-y-2">
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject line" data-testid="newsletter-broadcast-subject" className="rounded-lg" />
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} placeholder="Message body. New lines are preserved." data-testid="newsletter-broadcast-body" className="rounded-lg resize-none" />
          <Button
            onClick={sendBroadcast}
            disabled={sending || items.length === 0}
            data-testid="newsletter-broadcast-send"
            className="rounded-full bg-aawta-magenta hover:bg-aawta-magenta-hover text-white"
          >
            <Send className="h-4 w-4 mr-2" /> {sending ? 'Sending…' : `Send to ${items.length} subscribers`}
          </Button>
        </div>

        {broadcasts.length > 0 && (
          <div className="mt-5 pt-5 border-t border-aawta-border" data-testid="newsletter-broadcast-history">
            <div className="text-[11px] uppercase tracking-wider text-aawta-muted font-semibold mb-2">
              Broadcast history ({broadcasts.length})
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {broadcasts.map((b) => (
                <details key={b.id} className="rounded-lg bg-aawta-surface border border-aawta-border p-3 text-[13px]">
                  <summary className="cursor-pointer flex flex-wrap items-center gap-x-3 gap-y-1 text-aawta-navy">
                    <span className="font-semibold truncate">{b.subject}</span>
                    <span className="text-[11.5px] text-aawta-muted">
                      {new Date(b.sent_at).toLocaleString()} · {b.recipient_count} recipients · by {b.sent_by}
                    </span>
                  </summary>
                  <div className="mt-2 whitespace-pre-wrap text-aawta-muted text-[13px] leading-relaxed">{b.body}</div>
                </details>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-aawta-border bg-white overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 text-[11px] uppercase tracking-wider font-semibold text-aawta-muted bg-aawta-surface">
          <div className="col-span-7">Email</div>
          <div className="col-span-4">Signed up</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-aawta-muted">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-aawta-muted">No subscribers yet.</div>
        ) : filtered.map((s) => (
          <div key={s.id} data-testid={`newsletter-row-${s.id}`} className="grid grid-cols-1 md:grid-cols-12 gap-3 px-5 py-3 border-t border-aawta-border hover:bg-aawta-surface">
            <div className="md:col-span-7 font-medium text-aawta-navy truncate">{s.email}</div>
            <div className="md:col-span-4 text-[12.5px] text-aawta-muted">{new Date(s.created_at).toLocaleString()}</div>
            <div className="md:col-span-1 flex md:justify-end">
              <Button size="sm" variant="ghost" onClick={() => remove(s.id)} className="rounded-full h-8 text-rose-600 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- Admin Access Panel (Primary Admin only) ----------
const AdminAccessPanel = () => {
  const [data, setData] = useState({ super_admin_email: '', admins: [], is_super_admin: false });
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/admins');
      setData(data);
    } catch { toast.error('Failed to load admins'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addAdmin = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    setSaving(true);
    try {
      await api.post('/admin/admins', { email: newEmail.trim(), name: newName.trim() || null, note: note.trim() || null });
      toast.success('Admin authorized');
      setNewEmail(''); setNewName(''); setNote('');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add admin');
    } finally { setSaving(false); }
  };

  const toggleActive = async (row) => {
    try {
      await api.patch(`/admin/admins/${row.id}`, { active: !row.active });
      toast.success(row.active ? 'Deactivated' : 'Reactivated');
      await load();
    } catch { toast.error('Update failed'); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove admin access for ${row.email}?\n\nAny active session will be signed out immediately.`)) return;
    try {
      await api.delete(`/admin/admins/${row.id}`);
      toast.success('Removed');
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Delete failed'); }
  };

  if (!data.is_super_admin) {
    return (
      <div className="rounded-2xl bg-white border border-aawta-border p-8 text-center" data-testid="admin-access-locked">
        <div className="w-12 h-12 rounded-xl bg-aawta-magenta/10 text-aawta-magenta flex items-center justify-center mx-auto">
          <User className="h-5 w-5" />
        </div>
        <div className="mt-3 font-display font-semibold text-aawta-navy text-lg">Primary Admin only</div>
        <p className="mt-1 text-[13.5px] text-aawta-muted max-w-md mx-auto">
          Managing admin access is restricted to the Primary Admin ({data.super_admin_email}).
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-access-panel">
      <div>
        <div className="font-display font-semibold text-xl text-aawta-navy">Manage Admin Access</div>
        <p className="text-[13px] text-aawta-muted mt-1">
          Authorize additional AAWTA Gmail addresses to sign in with &ldquo;Continue with Google&rdquo; and access this Command Center.
          Only the Primary Admin ({data.super_admin_email}) can add or remove administrators.
        </p>
      </div>

      <form onSubmit={addAdmin} className="rounded-2xl bg-white border border-aawta-border p-5">
        <div className="text-[11px] uppercase tracking-wider text-aawta-muted font-semibold">Authorize new admin</div>
        <div className="mt-3 grid md:grid-cols-3 gap-3">
          <div className="md:col-span-1">
            <Label className="text-[12px]">Gmail address <span className="text-aawta-magenta">*</span></Label>
            <Input required type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
              placeholder="name@gmail.com" data-testid="admin-access-email" className="mt-1.5 rounded-lg" />
          </div>
          <div className="md:col-span-1">
            <Label className="text-[12px]">Display name (optional)</Label>
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Full name"
              data-testid="admin-access-name" className="mt-1.5 rounded-lg" />
          </div>
          <div className="md:col-span-1">
            <Label className="text-[12px]">Role / note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Program Manager"
              data-testid="admin-access-note" className="mt-1.5 rounded-lg" />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button type="submit" disabled={saving || !newEmail.trim()} data-testid="admin-access-add"
            className="rounded-full bg-aawta-magenta hover:bg-aawta-magenta-hover text-white">
            <Plus className="h-4 w-4 mr-2" /> {saving ? 'Authorizing…' : 'Authorize admin'}
          </Button>
        </div>
      </form>

      <div className="rounded-2xl border border-aawta-border bg-white overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 text-[11px] uppercase tracking-wider font-semibold text-aawta-muted bg-aawta-surface">
          <div className="col-span-4">Email</div>
          <div className="col-span-3">Name / role</div>
          <div className="col-span-2">Added by</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>

        {/* Primary admin row — always first, never removable */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 px-5 py-4 border-t border-aawta-border bg-aawta-magenta/5" data-testid="admin-access-primary">
          <div className="md:col-span-4 font-medium text-aawta-navy truncate">{data.super_admin_email}</div>
          <div className="md:col-span-3 text-[13px] text-aawta-navy">Primary Admin (you)</div>
          <div className="md:col-span-2 text-[12.5px] text-aawta-muted">—</div>
          <div className="md:col-span-1">
            <span className="text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Active</span>
          </div>
          <div className="md:col-span-2 flex md:justify-end">
            <span className="text-[11.5px] text-aawta-muted italic">Cannot be removed</span>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-aawta-muted">Loading…</div>
        ) : data.admins.length === 0 ? (
          <div className="p-8 text-center text-aawta-muted border-t border-aawta-border">
            No additional admins yet. Add one above to grant access.
          </div>
        ) : data.admins.map((row) => (
          <div key={row.id} data-testid={`admin-access-row-${row.id}`}
               className="grid grid-cols-1 md:grid-cols-12 gap-3 px-5 py-4 border-t border-aawta-border hover:bg-aawta-surface">
            <div className="md:col-span-4 font-medium text-aawta-navy truncate">{row.email}</div>
            <div className="md:col-span-3 text-[13px] text-aawta-navy">
              {row.name || <span className="text-aawta-muted">—</span>}
              {row.note && <div className="text-[12px] text-aawta-muted">{row.note}</div>}
            </div>
            <div className="md:col-span-2 text-[12.5px] text-aawta-muted truncate">{row.added_by}</div>
            <div className="md:col-span-1">
              <span className={`text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                row.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'
              }`}>{row.active ? 'Active' : 'Inactive'}</span>
            </div>
            <div className="md:col-span-2 flex md:justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => toggleActive(row)}
                      data-testid={`admin-access-toggle-${row.id}`}
                      className="rounded-full border-aawta-border h-8 text-[12px]">
                {row.active ? <><EyeOff className="h-3.5 w-3.5 mr-1" /> Deactivate</> : <><Eye className="h-3.5 w-3.5 mr-1" /> Reactivate</>}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => remove(row)}
                      data-testid={`admin-access-remove-${row.id}`}
                      className="rounded-full h-8 text-rose-600 hover:bg-rose-50">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- Dashboard shell ----------
const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const doLogout = async () => {
    await logout();
    window.location.href = '/admin/login';
  };

  return (
    <div className="min-h-screen bg-aawta-surface">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-aawta-border">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={AAWTA.logoUrl} alt="AAWTA" className="h-9 w-9 rounded-full object-cover" />
            <div className="hidden sm:block">
              <div className="font-display font-bold text-aawta-navy leading-none">AAWTA Admin</div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-aawta-muted font-medium">Workforce Collaborative</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" data-testid="admin-view-site" className="hidden sm:inline-flex items-center gap-1.5 text-[13px] text-aawta-navy hover:text-aawta-magenta">
              <ExternalLink className="h-3.5 w-3.5" /> View site
            </a>
            <div className="hidden md:flex items-center gap-2 text-[13px] text-aawta-muted">
              <User className="h-4 w-4" />
              <span className="max-w-[220px] truncate">{user?.email}</span>
            </div>
            <Button onClick={doLogout} variant="outline" data-testid="admin-logout" className="rounded-full border-aawta-border">
              <LogOut className="h-4 w-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-8 py-8">
        <div className="mb-6">
          <span className="overline text-aawta-magenta">Future Workforce Summit 2026</span>
          <h1 className="font-display font-bold text-3xl md:text-4xl mt-2 text-aawta-navy tracking-tight">
            Admin Dashboard
          </h1>
        </div>

        <Tabs defaultValue="overview">
          <TabsList data-testid="admin-tabs" className="bg-white border border-aawta-border rounded-full p-1 flex-wrap h-auto">
            <TabsTrigger value="overview" data-testid="tab-overview" className="rounded-full data-[state=active]:bg-aawta-navy data-[state=active]:text-white">Overview</TabsTrigger>
            <TabsTrigger value="submissions" data-testid="tab-submissions" className="rounded-full data-[state=active]:bg-aawta-navy data-[state=active]:text-white">Submissions</TabsTrigger>
            <TabsTrigger value="sponsors" data-testid="tab-sponsors" className="rounded-full data-[state=active]:bg-aawta-navy data-[state=active]:text-white">Sponsors</TabsTrigger>
            <TabsTrigger value="exhibitors" data-testid="tab-exhibitors" className="rounded-full data-[state=active]:bg-aawta-navy data-[state=active]:text-white">Exhibitors</TabsTrigger>
            <TabsTrigger value="speakers" data-testid="tab-speakers" className="rounded-full data-[state=active]:bg-aawta-navy data-[state=active]:text-white">Speakers</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery" className="rounded-full data-[state=active]:bg-aawta-navy data-[state=active]:text-white">Gallery</TabsTrigger>
            <TabsTrigger value="downloads" data-testid="tab-downloads" className="rounded-full data-[state=active]:bg-aawta-navy data-[state=active]:text-white">Downloads</TabsTrigger>
            <TabsTrigger value="volunteer" data-testid="tab-volunteer" className="rounded-full data-[state=active]:bg-aawta-navy data-[state=active]:text-white">Volunteers</TabsTrigger>
            <TabsTrigger value="media" data-testid="tab-media" className="rounded-full data-[state=active]:bg-aawta-navy data-[state=active]:text-white">Media</TabsTrigger>
            <TabsTrigger value="newsletter" data-testid="tab-newsletter" className="rounded-full data-[state=active]:bg-aawta-navy data-[state=active]:text-white">Newsletter</TabsTrigger>
            {user?.is_super_admin && (
              <TabsTrigger value="admins" data-testid="tab-admins" className="rounded-full data-[state=active]:bg-aawta-navy data-[state=active]:text-white">Admin Access</TabsTrigger>
            )}
          </TabsList>
          <TabsContent value="overview" className="mt-6"><OverviewPanel /></TabsContent>
          <TabsContent value="submissions" className="mt-6"><SubmissionsPanel /></TabsContent>
          <TabsContent value="sponsors" className="mt-6"><CmsPanel resource="sponsors" label="Sponsors" singular="Sponsor" fields={SPONSOR_FIELDS} uploadFolder="logos" testIdKey="sponsor" /></TabsContent>
          <TabsContent value="exhibitors" className="mt-6"><CmsPanel resource="exhibitors" label="Exhibitors" singular="Exhibitor" fields={EXHIBITOR_FIELDS} uploadFolder="logos" testIdKey="exhibitor" /></TabsContent>
          <TabsContent value="speakers" className="mt-6"><CmsPanel resource="speakers" label="Speakers" singular="Speaker" fields={SPEAKER_FIELDS} uploadFolder="headshots" testIdKey="speaker" /></TabsContent>
          <TabsContent value="gallery" className="mt-6"><CmsPanel resource="gallery" label="Gallery" singular="Gallery Item" fields={GALLERY_FIELDS} uploadFolder="banners" testIdKey="gallery" /></TabsContent>
          <TabsContent value="downloads" className="mt-6"><CmsPanel resource="downloads" label="Downloads" singular="Download" fields={DOWNLOAD_FIELDS} uploadFolder="downloads" testIdKey="download" /></TabsContent>
          <TabsContent value="volunteer" className="mt-6"><ApplicationsPanel resource="volunteer" kind="volunteer applications" assignmentLabel="Assigned role" assignmentOptions={VOLUNTEER_ROLES} /></TabsContent>
          <TabsContent value="media" className="mt-6"><ApplicationsPanel resource="media" kind="media applications" assignmentLabel="Credential type" assignmentOptions={MEDIA_CREDENTIALS} /></TabsContent>
          <TabsContent value="newsletter" className="mt-6"><NewsletterPanel /></TabsContent>
          {user?.is_super_admin && (
            <TabsContent value="admins" className="mt-6"><AdminAccessPanel /></TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboard;
